import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { skills } from "../data/portfolio";

const colorMap = {
  "#60a5fa": {
    bg: "rgba(96,165,250,0.08)",
    border: "rgba(96,165,250,0.25)",
    text: "#93c5fd",
  },
  "#a78bfa": {
    bg: "rgba(167,139,250,0.08)",
    border: "rgba(167,139,250,0.25)",
    text: "#c4b5fd",
  },
  "#34d399": {
    bg: "rgba(52,211,153,0.08)",
    border: "rgba(52,211,153,0.25)",
    text: "#6ee7b7",
  },
  "#f472b6": {
    bg: "rgba(244,114,182,0.08)",
    border: "rgba(244,114,182,0.25)",
    text: "#f9a8d4",
  },
  "#fbbf24": {
    bg: "rgba(251,191,36,0.08)",
    border: "rgba(251,191,36,0.25)",
    text: "#fcd34d",
  },
};

function SkillGroup({ group, groupIndex }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const colors = colorMap[group.color] || colorMap["#60a5fa"];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.6,
        delay: groupIndex * 0.08,
        ease: [0.23, 1, 0.32, 1],
      }}
      className="glass-card p-6"
    >
      {/* Category label */}
      <div className="flex items-center gap-2 mb-4">
        <div
          className="w-2 h-2 rounded-full"
          style={{
            background: group.color,
            boxShadow: `0 0 8px ${group.color}`,
          }}
        />
        <span
          className="font-mono text-[11px] tracking-[0.18em] uppercase"
          style={{ color: colors.text }}
        >
          {group.category}
        </span>
      </div>

      {/* Skill tags */}
      <div className="flex flex-wrap gap-2">
        {group.items.map((item, i) => (
          <motion.span
            key={item}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{
              duration: 0.4,
              delay: groupIndex * 0.08 + i * 0.04 + 0.2,
              ease: [0.23, 1, 0.32, 1],
            }}
            className="skill-badge px-3 py-1.5 rounded-full text-xs font-mono cursor-default"
            style={{
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}
          >
            {item}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Skills() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="skills" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div className="max-w-6xl mx-auto">
        <div ref={ref}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-label mb-3"
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">
              02 // Tech Stack
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4"
          >
            Tools of the
            <br />
            <span className="grad-text">trade.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/40 text-base max-w-xl mb-16"
          >
            I specialize in building scalable full-stack applications using
            MERN, with a strong focus on backend architecture, performance
            optimization, and solving real-world problems.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {skills.map((group, i) => (
            <SkillGroup key={group.category} group={group} groupIndex={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
